import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Flex } from 'components';
import { HookFormToggle, HookFormDropdown } from 'components/reactHookForm';
import { WorkOrderModuleConfiguration } from 'lane-shared/domains/workOrder/types/workOrder';
import { ServiceRequestStatusEnum } from 'lane-shared/domains/workOrder/constants';
import { getServiceRequestStatusLabel } from 'domains-work-order-type-contracts';
import cx from 'classnames';
import styles from '../Configuration.module.scss';

type AutoSurveyTriggerStatus =
  | ServiceRequestStatusEnum.Complete
  | ServiceRequestStatusEnum.Closed;

const REQUIRED_FIELD_INDICATOR = '*';

export const WorkOrderSurveys = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<WorkOrderModuleConfiguration>();

  const AUTO_SURVEY_TRIGGER_OPTIONS: Array<{
    label: string;
    value: AutoSurveyTriggerStatus;
  }> = [
    {
      label:
        getServiceRequestStatusLabel(ServiceRequestStatusEnum.Closed) ??
        ServiceRequestStatusEnum.Closed,
      value: ServiceRequestStatusEnum.Closed,
    },
    {
      label:
        getServiceRequestStatusLabel(ServiceRequestStatusEnum.Complete) ??
        ServiceRequestStatusEnum.Complete,
      value: ServiceRequestStatusEnum.Complete,
    },
  ];

  const allowFeedback = useWatch({
    control,
    name: 'workOrderSurveys.allowFeedback',
  });

  return (
    <div>
      <div className="py-1">
        <h2
          data-test="work-order-surveys-heading"
          className="text-lg font-medium text-gray-900"
        >
          {t('web.admin.work-order.configuration.workOrderSurveys.tab.title')}
        </h2>
      </div>

      <form>
        <div className={cx(styles.border, 'py-2 px-2 mt-2 space-y-2')}>
          <Flex align="center">
            <HookFormToggle
              control={control}
              name="workOrderSurveys.allowFeedback"
              testId="allow-feedback-toggle"
            />

            <div>
              <h3 className="text-base font-medium text-gray-900">
                {t(
                  'web.admin.work-order.configuration.workOrderSurveys.tab.title'
                )}
              </h3>
            </div>
          </Flex>

          {allowFeedback && (
            <>
              <div className="ml-12 space-y-3 !mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-medium text-gray-900">
                    {t(
                      'web.admin.work-order.configuration.workOrderSurveys.rule.autoSurveyOnStatus'
                    )}
                  </h3>
                </div>
              </div>
              <div className="ml-12">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t(
                    'web.admin.work-order.configuration.workOrderSurveys.rule.status'
                  )}
                  {allowFeedback && (
                    <span className={styles.required}>
                      {REQUIRED_FIELD_INDICATOR}
                    </span>
                  )}
                </label>
                <HookFormDropdown
                  control={control}
                  name="workOrderSurveys.autoSurveyTriggerStatus"
                  items={AUTO_SURVEY_TRIGGER_OPTIONS}
                  placeholder={t(
                    'web.admin.work-order.configuration.workOrderSurveys.rule.select'
                  )}
                  isRequired={allowFeedback}
                  returnValueOnly
                  testId="auto-survey-status-dropdown"
                />
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};
